import React from 'react';
import {
  View,
  Text,
  ScrollView,
  Pressable,
  StyleSheet,
  FlatList,
} from 'react-native';
import { useTranslation } from 'react-i18next';
import { usePantryName } from '../usePantryName';
import Ionicons from '@expo/vector-icons/Ionicons';
import {
  Paprika,
  PaprikaTypography,
  PaprikaShadows,
  PaprikaZones,
} from '../../../styles/theme';
import { PantrySnapshot } from '../../../redux/pantrySelectors';
import { ZONE_KEYS } from '../zoneMeta';
import { CookStrip } from '../CookStrip';
import { ZoneTile } from './ZoneTile';
import { ClearPantryRow } from '../ClearPantryRow';
import { HeaderAvatarButton } from '../../HeaderAvatarButton';

interface Props {
  snapshot: PantrySnapshot;
  onOpenZone: (zone: any) => void;
  onOpenCook: () => void;
  onOpenReview: () => void;
  onAddWhere: () => void;
  onReceipt: () => void;
  duplicateCount?: number;
  onOpenDedupe?: () => void;
  hasDemoItems?: boolean;
  onRemoveDemoItems?: () => void;
  onClearPantry?: () => void;
  clearBusy?: boolean;
}

export function HomeView({
  snapshot,
  onOpenZone,
  onOpenCook,
  onOpenReview,
  onAddWhere,
  onReceipt,
  duplicateCount = 0,
  onOpenDedupe,
  hasDemoItems = false,
  onRemoveDemoItems,
  onClearPantry,
  clearBusy = false,
}: Props) {
  const { t } = useTranslation();
  const pantryName = usePantryName();
  const cookNames = snapshot.cookList.map((v) => pantryName(v.item));

  return (
    <View style={styles.root}>
      <ScrollView
        style={styles.scroll}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
      >
        {/* Header */}
        <View style={styles.header}>
          <View style={styles.titleSection}>
            <Text style={styles.title}>{t('pantry.title')}</Text>
            <Text style={styles.meta}>
              {t('pantry.itemsCount', { count: snapshot.totalCount })} · {t('pantry.upToDate')}
            </Text>
          </View>
          <View style={styles.headerActions}>
            <Pressable onPress={onReceipt} style={styles.receiptButton}>
              <Ionicons name="receipt" size={22} color={Paprika.textPrimary} />
            </Pressable>
            <HeaderAvatarButton />
          </View>
        </View>

        {/* Cook Strip */}
        <View style={styles.cookStripContainer}>
          <CookStrip
            count={snapshot.cookList.length}
            names={cookNames}
            onPress={onOpenCook}
          />
        </View>

        {/* Demo Banner */}
        {hasDemoItems && (
          <Pressable
            onPress={onRemoveDemoItems}
            style={[
              styles.demoBanner,
              { backgroundColor: Paprika.brandSoft, borderColor: Paprika.accent },
            ]}
          >
            <View style={styles.demoBannerContent}>
              <Text style={[styles.demoBannerLabel, { color: Paprika.accent }]}>
                {t('pantry.empty.demoBanner')}
              </Text>
              <Text style={styles.demoBannerSub}>
                {t('pantry.empty.demoBannerSub')}
              </Text>
            </View>
            <Text style={[styles.demoBannerLink, { color: Paprika.accent }]}>
              {t('pantry.empty.demoRemove')}
            </Text>
          </Pressable>
        )}

        {/* Spaces Eyebrow */}
        <Text style={styles.eyebrow}>{t('pantry.mySpaces')}</Text>

        {/* 2x2 Grid */}
        <View style={styles.gridContainer}>
          <View style={styles.gridRow}>
            <ZoneTile
              zone={ZONE_KEYS[0]}
              count={snapshot.byLocation[ZONE_KEYS[0]].length}
              urgentCount={
                snapshot.urgentCountByLocation[ZONE_KEYS[0]]
              }
              onPress={() => onOpenZone(ZONE_KEYS[0])}
            />
            <ZoneTile
              zone={ZONE_KEYS[1]}
              count={snapshot.byLocation[ZONE_KEYS[1]].length}
              urgentCount={
                snapshot.urgentCountByLocation[ZONE_KEYS[1]]
              }
              onPress={() => onOpenZone(ZONE_KEYS[1])}
            />
          </View>
          <View style={styles.gridRow}>
            <ZoneTile
              zone={ZONE_KEYS[2]}
              count={snapshot.byLocation[ZONE_KEYS[2]].length}
              urgentCount={
                snapshot.urgentCountByLocation[ZONE_KEYS[2]]
              }
              onPress={() => onOpenZone(ZONE_KEYS[2])}
            />
            <ZoneTile
              zone={ZONE_KEYS[3]}
              count={snapshot.byLocation[ZONE_KEYS[3]].length}
              urgentCount={
                snapshot.urgentCountByLocation[ZONE_KEYS[3]]
              }
              onPress={() => onOpenZone(ZONE_KEYS[3])}
            />
          </View>
        </View>

        {/* Review Row */}
        {snapshot.reviewList.length > 0 && (
          <Pressable
            onPress={onOpenReview}
            style={[
              styles.reviewRow,
              { backgroundColor: Paprika.aiSoft, borderColor: Paprika.ai },
            ]}
          >
            <Ionicons name="sparkles" size={18} color={Paprika.ai} />
            <View style={styles.reviewContent}>
              <Text style={[styles.reviewLabel, { color: Paprika.ai }]}>
                {t('pantry.review.entry')}
              </Text>
              <Text style={styles.reviewCount}>
                {t('pantry.review.entrySub', { count: snapshot.reviewList.length })}
              </Text>
            </View>
            <Pressable
              onPress={onOpenReview}
              hitSlop={8}
            >
              <Text style={[styles.reviewLink, { color: Paprika.ai }]}>
                {t('pantry.review.entryCta')}
              </Text>
            </Pressable>
          </Pressable>
        )}

        {/* Duplicates Row */}
        {duplicateCount > 0 && onOpenDedupe && (
          <Pressable
            onPress={onOpenDedupe}
            style={[
              styles.reviewRow,
              { backgroundColor: Paprika.surfaceLow, borderColor: Paprika.textMuted },
            ]}
          >
            <Ionicons name="copy-outline" size={18} color={Paprika.textSecondary} />
            <View style={styles.reviewContent}>
              <Text style={[styles.reviewLabel, { color: Paprika.textSecondary }]}>
                {t('pantry.tidy.entry')}
              </Text>
              <Text style={styles.reviewCount}>
                {t('pantry.tidy.entrySub', { count: duplicateCount })}
              </Text>
            </View>
            <Text style={[styles.reviewLink, { color: Paprika.textSecondary }]}>
              {t('pantry.tidy.entryCta')}
            </Text>
          </Pressable>
        )}

        {/* Clear pantry — last on the screen, where irreversible things belong */}
        {onClearPantry && (
          <ClearPantryRow
            count={snapshot.totalCount}
            busy={clearBusy}
            onPress={onClearPantry}
          />
        )}
      </ScrollView>

      {/* FAB */}
      <Pressable
        onPress={onAddWhere}
        style={({ pressed }) => [
          styles.fab,
          {
            backgroundColor: Paprika.accent,
            transform: [{ scale: pressed ? 0.93 : 1 }],
          },
        ]}
      >
        <Ionicons name="add" size={28} color={Paprika.onPrimary} />
      </Pressable>
    </View>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: Paprika.background,
  },
  scroll: {
    flex: 1,
  },
  scrollContent: {
    paddingHorizontal: 16,
    paddingTop: 16,
    paddingBottom: 100,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    marginBottom: 16,
  },
  titleSection: {
    flex: 1,
  },
  headerActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  title: {
    ...PaprikaTypography.displayMedium,
    color: Paprika.textPrimary,
    marginBottom: 4,
  },
  meta: {
    fontFamily: 'Inter-Regular',
    fontSize: 13,
    color: Paprika.textSecondary,
  },
  receiptButton: {
    width: 42,
    height: 42,
    borderRadius: 21,
    backgroundColor: Paprika.surfaceLight,
    alignItems: 'center',
    justifyContent: 'center',
    marginLeft: 12,
  },
  cookStripContainer: {
    marginBottom: 24,
  },
  eyebrow: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 11,
    lineHeight: 14,
    letterSpacing: 0.6,
    textTransform: 'uppercase',
    color: Paprika.textMuted,
    marginBottom: 13,
  },
  gridContainer: {
    gap: 11,
    marginBottom: 24,
  },
  gridRow: {
    flexDirection: 'row',
    gap: 11,
  },
  reviewRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingVertical: 12,
    paddingHorizontal: 14,
    borderRadius: 14,
    borderWidth: 1,
    marginBottom: 24,
  },
  reviewContent: {
    flex: 1,
  },
  reviewLabel: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 13,
    lineHeight: 16,
  },
  reviewCount: {
    fontFamily: 'Inter-Regular',
    fontSize: 11,
    color: Paprika.textSecondary,
    marginTop: 2,
  },
  reviewLink: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 12,
  },
  fab: {
    position: 'absolute',
    bottom: 82,
    right: 18,
    width: 54,
    height: 54,
    borderRadius: 27,
    alignItems: 'center',
    justifyContent: 'center',
    ...PaprikaShadows.cardHover,
  },
  demoBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingVertical: 12,
    paddingHorizontal: 14,
    borderRadius: 14,
    borderWidth: 1,
    marginBottom: 24,
  },
  demoBannerContent: {
    flex: 1,
  },
  demoBannerLabel: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 13,
    lineHeight: 16,
  },
  demoBannerSub: {
    fontFamily: 'Inter-Regular',
    fontSize: 11,
    color: Paprika.textSecondary,
    marginTop: 2,
  },
  demoBannerLink: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 12,
  },
});
